import { FileProcessUpdate } from '@/validation/fileProcessUpdate';
import { IonGrid, IonSplitPane } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { SelectDataFileActionSection, SelectDataFileInformationSection } from '@/components/SelectDataFileSections';
import { SensorFlowModal } from '@/components/SensorFlowModal/SensorFlowModal';
import { SensorSidebar } from '@/components/SensorSidebar/SensorSidebar';

import { mergeClassNames } from '@/utils';

import { hubSensorService } from '@/controllers/sensorService';

import { useExitSession } from '@/hooks/useExitSession';
import { useIsOnline } from '@/hooks/useIsOnline';

import Step1BackgroundImage from '@/assets/illustrations/upload-data-1.png';
import Step2BackgroundImage from '@/assets/illustrations/upload-data-2.png';

import styles from './ProcessAndUploadScreen.module.css';

interface ProcessAndUploadScreenProps {
  isHubFlow: boolean;
}

export const ProcessAndUploadScreen = ({ isHubFlow }: ProcessAndUploadScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wentOfflineDuringUpload, setWentOfflineDuringUpload] = useState(false);
  const [isDataError, setIsDataError] = useState(false);
  const isOnline = useIsOnline();
  const { exit } = useExitSession(isHubFlow);

  const [syncingProgress, setSyncingProgress] = useState(10);

  const history = useHistory();

  // Only show this screen on the hub flow (you should not be able to get here otherwise (failsafe))
  if (!isHubFlow) {
    history.push('/dashboard');
  }

  // We don't know if the hub is plugged in, so after three seconds we assume it is, and continue to the next screen
  useEffect(() => {
    setTimeout(() => {
      setCurrentStep(1);
    }, 3000);
  }, []);

  const onUploadFile = () => {
    hubSensorService.downloadFile(() => {
      hubSensorService.removeListener('onFileSelected--download');
      setCurrentStep(2);

      setSyncingProgress(0.2);
    });
  };

  useEffect(() => {
    const handleUpdate = (e: FileProcessUpdate) => {
      console.log('fileProcessUpdate event:', e);
      if (e.variant === 'loading' && typeof e.progress === 'number') {
        setSyncingProgress(Math.max(0.1, e.progress));
      }
      if (e.variant === 'success' && typeof e.progress === 'number' && e.progress === 1) {
        setSyncingProgress(1);
      }
      if (e.variant === 'error') {
        setSyncingProgress(0);
        setCurrentStep(3);
        setIsDataError(true);
      }
    };

    hubSensorService.on('fileProcessUpdate', handleUpdate);

    return () => {
      hubSensorService.off('fileProcessUpdate', handleUpdate);
    };
  }, []);

  const onSyncingComplete = () => {
    setCurrentStep(3);
  };

  const onEndSession = () => {
    exit();

    setTimeout(() => {
      history.goBack();
    }, 500);
  };

  const onEndSessionWithoutInternet = () => {
    onEndSession();
  };

  // * Monitor the internet connection,
  // * so that when the user is at the uploading step, and goes offline, we set setWentOfflineDuringUpload to true
  useEffect(() => {
    if (currentStep === 2) {
      setWentOfflineDuringUpload(!isOnline);
    }
  }, [isOnline, currentStep]);

  // Continue to the next screen when the syncing is complete
  useEffect(() => {
    if (syncingProgress === 1) {
      onSyncingComplete();
    }
  }, [syncingProgress]);

  return (
    <IonSplitPane className={styles.splitPane} contentId='recording' when='xs'>
      <SensorSidebar disabled={true} isHidden={currentStep === 1} isHubFlow={isHubFlow} />

      <div className={mergeClassNames('ion-page', 'ion-no-padding', styles.content)} id='recording'>
        <IonGrid className={mergeClassNames('ion-no-padding', styles.grid)} fixed>
          {currentStep === 0 && (
            <div className={styles.center}>
              <SensorFlowModal description='Plug in the Sensor Hub' illustration='plugIn' />
            </div>
          )}

          {currentStep === 1 && (
            <div className={styles.fullScreen}>
              <SelectDataFileActionSection
                buttonOnclick={() => onUploadFile()}
                buttonText='Download file'
                description='To complete the session and sync your hub’s data, tap *“Download file”* and select the data file on your device.'
                title='Download data file'
              />

              <SelectDataFileInformationSection
                steps={[
                  {
                    index: 1,
                    image: Step1BackgroundImage,
                    title: 'Open the Sensor Hub location',
                    description: 'In your files select *QSENSEHUB* under the *locations* section on the left panel.',
                  },
                  {
                    index: 2,
                    image: Step2BackgroundImage,
                    title: 'Select the data file',
                    description:
                      'The right panel will show the files within, select *data* to allow connection with the hub.',
                  },
                ]}
              />
            </div>
          )}

          {currentStep === 2 && (
            <div className={styles.center}>
              <SensorFlowModal
                description='Sensor Hub is syncing...'
                illustration='plugInSyncing'
                progress={syncingProgress}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div className={styles.center}>
              {isDataError ? (
                <SensorFlowModal
                  description='An error occurred while processing the data. Please re-take the measurements.'
                  illustration='error'
                  primaryButton={{
                    label: 'End session',
                    onClick: onEndSession,
                  }}
                />
              ) : wentOfflineDuringUpload ? (
                <SensorFlowModal
                  description='Data has not been sent to the cloud. Upload will resume when connection is recovered.'
                  illustration='error'
                  primaryButton={{
                    label: 'End session',
                    onClick: onEndSessionWithoutInternet,
                  }}
                />
              ) : (
                <SensorFlowModal
                  description='Recordings successfully processed'
                  illustration='success'
                  primaryButton={{
                    label: 'End session',
                    onClick: onEndSession,
                  }}
                />
              )}
            </div>
          )}
        </IonGrid>
      </div>
    </IonSplitPane>
  );
};
